"use client";

import { useRef } from "react";
import { ArrowUpRight, Play } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
export default function Hero() {
  const container = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".hero-eyebrow", {
        y: 16
      });
      gsap.to(".hero-eyebrow", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        delay: 0.1
      });
      gsap.set(".hero-word", {
        y: 40
      });
      gsap.to(".hero-word", {
        opacity: 1,
        y: 0,
        duration: 0.72,
        stagger: 0.04,
        ease: "power3.out",
        delay: 0.2
      });
      gsap.to(".cgi-ground", {
        xPercent: 3,
        yPercent: -2,
        duration: 22,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      });
      gsap.to(".cgi-ground", {
        opacity: 1,
        duration: 1.2,
        ease: "power2.out"
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".hero-eyebrow", ".hero-word"], {
        opacity: 1,
        y: 0
      });
      gsap.set(".cgi-ground", {
        opacity: 1
      });
    });
  }, {
    scope: container
  });
  const headlineWords = ["Rações", "de", "Animais."];
  return <section data-section-id="26909" ref={container} id="animalfeedintro" data-nav="dark" className="relative min-h-[92vh] w-full overflow-hidden bg-primary text-primary-foreground">
      {}
      <div className="cgi-ground absolute inset-0 z-0" style={{
      opacity: 0,
      background: "radial-gradient(ellipse 80% 60% at 15% 90%, hsl(215 90% 45% / 0.55) 0%, hsl(215 80% 20% / 0.35) 25%, hsl(220 22% 6% / 0.4) 55%, hsl(215 80% 20%) 80%)"
    }} />

      {}
      <div className="absolute inset-0 z-0 pointer-events-none" style={{
      background: "radial-gradient(circle at 10% 100%, hsl(215 90% 45% / 0.28) 0%, transparent 45%)"
    }} />

      {}
      <div className="absolute inset-0 z-0 opacity-[0.15] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "radial-gradient(hsl(210 10% 92% / 0.3) 1px, transparent 1px)",
      backgroundSize: "3px 3px"
    }} />

      {}
      <div className="relative z-10 mx-auto flex min-h-[92vh] w-full max-w-[1320px] flex-col px-6 py-32 md:px-10 md:py-44">
        <div className="mt-auto grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-end lg:gap-8">
          {}
          <div className="lg:col-span-7">
            <div className="hero-eyebrow mb-6 text-xs font-medium uppercase md:mb-8" style={{
            opacity: 0,
            color: "hsl(215 12% 70%)",
            letterSpacing: "0.22em"
          }}>
              Alimentação & Bebidas — Eurocontrol
            </div>

            <h1 className="font-default" style={{
            fontSize: "clamp(2.75rem, 5.4vw + 0.5rem, 6.5rem)",
            lineHeight: 0.98,
            letterSpacing: "-0.035em",
            fontWeight: 450,
            color: "hsl(210 20% 98%)"
          }}>
              {headlineWords.map((word, i) => <span key={i} data-index={i} className="hero-word inline-block" style={{
              opacity: 0,
              marginRight: "0.28em"
            }}>
                  {word}
                </span>)}
            </h1>
          </div>

          {}
          <div className="lg:col-span-5 lg:flex lg:justify-end">
            <motion.div initial={prefersReducedMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16,
            filter: "blur(12px)"
          }} animate={{
            opacity: 1,
            y: 0,
            filter: "blur(0px)"
          }} transition={{
            duration: 0.9,
            delay: 0.24,
            ease: [0.22, 0.61, 0.36, 1]
          }} className="relative w-full max-w-[420px]">
              {}
              <div className="absolute -inset-px rounded-2xl pointer-events-none" style={{
              background: "linear-gradient(135deg, hsl(215 90% 45% / 0.35) 0%, transparent 40%, transparent 60%, hsl(215 12% 70% / 0.2) 100%)",
              filter: "blur(1px)"
            }} />

              <div className="relative rounded-2xl p-8 md:p-10" style={{
              backgroundColor: "hsl(220 18% 16% / 0.6)",
              backdropFilter: "blur(32px) saturate(140%)",
              WebkitBackdropFilter: "blur(32px) saturate(140%)",
              boxShadow: "inset 0 0 0 1px hsl(220 18% 22% / 0.4)"
            }}>
                <p className="font-default" style={{
                color: "hsl(215 12% 70%)",
                fontSize: "clamp(1.0625rem, 0.35vw + 1rem, 1.1875rem)",
                lineHeight: 1.55,
                letterSpacing: "-0.005em",
                fontWeight: 450
              }}>
                  Tecnologia de pulverização para aplicação uniforme de gorduras, melaços, enzimas, vitaminas e aromas em pellets e farinhas, com doseamento preciso, condicionamento a vapor e minimização de desperdício.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button asChild className="group h-auto rounded-lg px-6 py-3.5 font-medium hover:shadow-none" style={{
                  backgroundColor: "hsl(215 90% 55%)",
                  color: "hsl(220 22% 6%)",
                  letterSpacing: "-0.005em"
                }}>
                    <Link to="/ContactsPage" className="inline-flex items-center gap-3">
                      <span>Consulte-nos</span>
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg transition-transform duration-300 group-hover:rotate-[-45deg]" style={{
                      border: "1px solid hsl(220 22% 6% / 0.24)"
                    }}>
                        <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                      </span>
                    </Link>
                  </Button>

                  <Button asChild variant="ghost" className="group h-auto rounded-lg px-6 py-3.5 font-medium hover:shadow-none hover:bg-transparent" style={{
                  backgroundColor: "hsl(220 18% 16% / 0.6)",
                  color: "hsl(210 20% 98%)",
                  boxShadow: "inset 0 0 0 1px hsl(220 18% 22% / 0.4)",
                  letterSpacing: "-0.005em"
                }}>
                    <Link to="/ProductsPage" className="inline-flex items-center gap-3">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg" style={{
                      backgroundColor: "hsl(220 18% 16% / 0.8)"
                    }}>
                        <Play className="h-2.5 w-2.5 fill-current" strokeWidth={0} />
                      </span>
                      <span>Ver produtos</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {}
        <div className="mt-16 flex items-center justify-between border-t pt-6 md:mt-24" style={{
        borderColor: "hsl(220 18% 22% / 0.4)"
      }}>
          <span className="text-xs uppercase" style={{
          color: "hsl(215 12% 70%)",
          letterSpacing: "0.22em",
          fontWeight: 500
        }}>
            {"Engenharia \u2022 Pulverização \u2022 Indústria"}
          </span>
          <span className="hidden text-xs uppercase md:inline-block" style={{
          color: "hsl(215 12% 70%)",
          letterSpacing: "0.22em",
          fontWeight: 500
        }}>
            Explore as soluções
          </span>
        </div>
      </div>
    </section>;
}